import PropTypes from 'prop-types';
import { prioritizeBioItems } from './enrichment-utils';
import EnrichmentConfidencePill from './enrichment-confidence-pill';

/**
 * Formats a location object into a display string
 * Preference order: City, State > Metro > Country > next highest weight record
 */
const formatLocation = (location) => {
  if (!location) return null;

  // Preference 1: City, State
  if (location.city && location.state) {
    return `${location.city}, ${location.state}`;
  }

  // Preference 2: Metro
  if (location.metro) {
    return location.metro;
  }

  // Preference 3: Country
  if (location.country) {
    return location.country;
  }

  // Preference 4: City only (if exists without state)
  if (location.city) {
    return location.city;
  }

  // Preference 5: State only (if exists without city)
  if (location.state) {
    return location.state;
  }

  return null;
};

/**
 * Gets the best location from an array of locations
 * Returns the first location with City+State, or falls back to Metro, Country, etc.
 */
const getBestLocation = (locations) => {
  if (!locations || locations.length === 0) return null;

  const sortedLocations = prioritizeBioItems(locations);

  // First try to find any location with City, State
  const cityStateLocation = sortedLocations.find((loc) => loc.city && loc.state);
  if (cityStateLocation) return cityStateLocation;

  // Then try Metro
  const metroLocation = sortedLocations.find((loc) => loc.metro);
  if (metroLocation) return metroLocation;

  // Then try Country
  const countryLocation = sortedLocations.find((loc) => loc.country);
  if (countryLocation) return countryLocation;

  // Otherwise return the highest weighted/most recent
  return sortedLocations[0];
};

const PersonalDetailsSection = ({ names = [], locations = [] }) => {
  // Get highest-weighted name
  const sortedNames = prioritizeBioItems(names);
  const topName = sortedNames.length > 0 ? sortedNames[0] : null;

  // Get best location according to preference order
  const bestLocation = getBestLocation(locations);
  const locationDisplay = formatLocation(bestLocation);

  // Check if both name and location are missing
  const hasNoData = !topName && !locationDisplay;

  // If no data available, show empty state
  if (hasNoData) {
    return (
      <div className="empty-state-message">
        <p className="empty-state-message--text">No enriched data available for this constituent</p>
      </div>
    );
  }

  return (
    <div className="personal-details-section">
      <table className="personal-details-section--table">
        <thead>
          <tr>
            <th className="personal-details-section--th">Detail</th>
            <th className="personal-details-section--th">Confidence</th>
          </tr>
        </thead>
        <tbody>
          <tr className="personal-details-section--row">
            <td className="personal-details-section--td personal-details-section--detail">
              <span className="personal-details-section--label">Name:</span> {topName ? topName.value : '-'}
            </td>
            <td className="personal-details-section--td personal-details-section--confidence">
              {topName && topName.confidence_score != null ? (
                <EnrichmentConfidencePill score={topName.confidence_score} />
              ) : (
                <span className="personal-details-section--dash">—</span>
              )}
            </td>
          </tr>
          <tr className="personal-details-section--row">
            <td className="personal-details-section--td personal-details-section--detail">
              <span className="personal-details-section--label">Location:</span> {locationDisplay || '-'}
            </td>
            <td className="personal-details-section--td personal-details-section--confidence">
              {bestLocation && bestLocation.confidence_score != null ? (
                <EnrichmentConfidencePill score={bestLocation.confidence_score} />
              ) : (
                <span className="personal-details-section--dash">—</span>
              )}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

PersonalDetailsSection.propTypes = {
  names: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      value: PropTypes.string,
      weight: PropTypes.number,
      confidence_score: PropTypes.number,
      created_at: PropTypes.string,
      rejected: PropTypes.bool,
    })
  ),
  locations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      city: PropTypes.string,
      state: PropTypes.string,
      metro: PropTypes.string,
      country: PropTypes.string,
      weight: PropTypes.number,
      confidence_score: PropTypes.number,
      created_at: PropTypes.string,
      rejected: PropTypes.bool,
    })
  ),
};

export default PersonalDetailsSection;
